import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SaveIcon from '@mui/icons-material/Save';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { routingApi } from '../api/admin';
import { AdminListPageHeader } from '../components/admin/adminListPageUi';
import {
  messageFromAdminConsoleAxiosError,
  messageFromOrchestratorAxiosError,
} from '../lib/axiosErrorMessage';
import { ConfigLabelSelect } from '../components/adapter/ConfigLabelSelect';
import {
  useAdapterConfigRouting,
  useUpdateAdapterConfigRouting,
} from '../hooks/useVendors';
import { useAdapterServiceCatalog } from '../hooks/useAdapterServices';
import {
  buildCatalogGroupsForLabel,
  normalizeConfigLabel,
} from '../lib/adapterCapabilityCatalog';
import type { RoutingServiceKey, VendorServiceRoutingVendor } from '../domain/vendor';
import { isRoutingServiceKey } from '../domain/vendor';

interface RoutingServiceOption {
  serviceKey: RoutingServiceKey;
  displayName: string;
  hasAdapterConfig: boolean;
}

function humanizeServiceKey(serviceKey: string): string {
  return serviceKey
    .split(/[-_]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function renumberRows(rows: VendorServiceRoutingVendor[]): VendorServiceRoutingVendor[] {
  return rows.map((row, index) => ({ ...row, priority: (index + 1) * 10 }));
}

function parsePriorityInput(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.trunc(n);
}

/**
 * RoutingPage
 * -----------
 * Service-level vendor switching plus the existing read-only doctrine routing matrix.
 */
export function RoutingPage() {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get('service');
  const catalogQuery = useAdapterServiceCatalog({ publishedOnly: true });
  const [selectedConfigLabel, setSelectedConfigLabel] = useState('default');
  const normalizedLabel = normalizeConfigLabel(selectedConfigLabel);

  const catalogGroups = useMemo(
    () => buildCatalogGroupsForLabel(catalogQuery.data ?? [], normalizedLabel),
    [catalogQuery.data, normalizedLabel],
  );

  const adapterRoutingQuery = useAdapterConfigRouting(normalizedLabel);
  const q = useQuery({ queryKey: ['routing'], queryFn: () => routingApi.list() });

  const serviceOptions = useMemo((): RoutingServiceOption[] => {
    const byKey = new Map<string, RoutingServiceOption>();

    for (const group of catalogGroups) {
      byKey.set(group.adapterServiceKey, {
        serviceKey: group.adapterServiceKey,
        displayName: group.serviceDisplayName,
        hasAdapterConfig: false,
      });
    }

    for (const service of adapterRoutingQuery.data?.services ?? []) {
      const existing = byKey.get(service.serviceKey);
      if (existing) {
        byKey.set(service.serviceKey, { ...existing, hasAdapterConfig: true });
      } else {
        byKey.set(service.serviceKey, {
          serviceKey: service.serviceKey,
          displayName: humanizeServiceKey(service.serviceKey),
          hasAdapterConfig: true,
        });
      }
    }

    return [...byKey.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName) || a.serviceKey.localeCompare(b.serviceKey),
    );
  }, [adapterRoutingQuery.data?.services, catalogGroups]);

  const [routingServiceKey, setRoutingServiceKey] = useState<RoutingServiceKey | ''>(() => {
    if (requestedService && isRoutingServiceKey(requestedService)) return requestedService;
    return '';
  });

  const updateAdapterRouting = useUpdateAdapterConfigRouting();
  const [rows, setRows] = useState<VendorServiceRoutingVendor[]>([]);

  useEffect(() => {
    if (serviceOptions.length === 0) {
      setRoutingServiceKey('');
      return;
    }
    if (routingServiceKey && serviceOptions.some(option => option.serviceKey === routingServiceKey)) {
      return;
    }
    const fromRequest =
      requestedService && isRoutingServiceKey(requestedService)
        ? serviceOptions.find(option => option.serviceKey === requestedService)?.serviceKey
        : undefined;
    setRoutingServiceKey(fromRequest ?? serviceOptions[0]?.serviceKey ?? '');
  }, [serviceOptions, routingServiceKey, requestedService]);

  const selectedService = useMemo(
    () => adapterRoutingQuery.data?.services.find(service => service.serviceKey === routingServiceKey),
    [adapterRoutingQuery.data?.services, routingServiceKey],
  );

  const serviceVendors = useMemo(
    () => selectedService?.vendors ?? [],
    [selectedService],
  );

  const enabledVendorCount = useMemo(
    () => rows.filter(row => row.enabled !== false).length,
    [rows],
  );

  useEffect(() => {
    setRows(serviceVendors);
  }, [serviceVendors]);

  const setRow = (vendorId: string, patch: Partial<VendorServiceRoutingVendor>) => {
    setRows(prev => prev.map(row =>
      row.vendorId === vendorId ? { ...row, ...patch } : row,
    ));
  };

  const moveRow = (vendorId: string, direction: -1 | 1) => {
    setRows(prev => {
      const ordered = [...prev].sort((a, b) => a.priority - b.priority || a.vendorId.localeCompare(b.vendorId));
      const index = ordered.findIndex(row => row.vendorId === vendorId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= ordered.length) return prev;
      const next = [...ordered];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return renumberRows(next);
    });
  };

  const saveSwitching = () => {
    if (!routingServiceKey) return;
    updateAdapterRouting.mutate({
      label: normalizedLabel,
      serviceKey: routingServiceKey,
      vendors: rows
        .map(row => ({
          vendorId: row.vendorId,
          priority: Math.max(1, Math.trunc(Number(row.priority) || 50)),
          enabled: row.enabled !== false,
        }))
        .sort((a, b) => a.priority - b.priority || a.vendorId.localeCompare(b.vendorId)),
    });
  };

  const selectedOption = serviceOptions.find(option => option.serviceKey === routingServiceKey);
  const canSave =
    Boolean(routingServiceKey) &&
    selectedOption?.hasAdapterConfig &&
    rows.length > 0 &&
    !updateAdapterRouting.isPending;

  const showSaveOrder = Boolean(routingServiceKey) && Boolean(selectedOption?.hasAdapterConfig);
  const saveOrderDisabledReason = updateAdapterRouting.isPending
    ? 'Saving…'
    : rows.length === 0
      ? 'No providers to reorder for this service and label.'
      : null;

  if (q.isPending || adapterRoutingQuery.isPending || catalogQuery.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (q.isError || adapterRoutingQuery.isError) {
    const message = q.isError
      ? messageFromOrchestratorAxiosError(q.error) || 'Failed to load routing rules.'
      : messageFromAdminConsoleAxiosError(adapterRoutingQuery.error) ||
        'Failed to load routing rules.';

    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => {
            q.refetch();
            adapterRoutingQuery.refetch();
          }}>
            Retry
          </Button>
        }
      >
        {message}
      </Alert>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Routing"
        subtitle="Configure service-level vendor switching, then review doctrine routing rules."
      />

      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Vendor Switching
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reads providers from adapter_configs for the selected label. Lower priority vendors are tried first.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            alignItems: 'start',
            gap: 2,
            mb: 0.5,
          }}
        >
          <ConfigLabelSelect
            selectOnly
            showHelperText={false}
            value={selectedConfigLabel}
            onChange={setSelectedConfigLabel}
          />
          {serviceOptions.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel>Adapter service</InputLabel>
              <Select
                value={routingServiceKey}
                label="Adapter service"
                onChange={event => {
                  const key = event.target.value;
                  if (isRoutingServiceKey(key)) setRoutingServiceKey(key);
                }}
              >
                {serviceOptions.map(option => (
                  <MenuItem key={option.serviceKey} value={option.serviceKey}>
                    {option.displayName} ({option.serviceKey})
                    {!option.hasAdapterConfig ? ' — no runtime config' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box />
          )}
          {showSaveOrder ? (
            <Tooltip title={saveOrderDisabledReason ?? ''} disableHoverListener={!saveOrderDisabledReason}>
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  startIcon={<SaveIcon />}
                  onClick={saveSwitching}
                  disabled={!canSave}
                >
                  Save Order
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Namespace in adapter_configs.label — publish and sync vendor config under this label.
        </Typography>

        {serviceOptions.length === 0 ? (
          <Alert severity="info">
            No services for label &quot;{normalizedLabel}&quot;. Publish Service Onboarding, sync adapter config from
            Provider Onboarding, or import config:service rows for this label.
          </Alert>
        ) : selectedOption && !selectedOption.hasAdapterConfig ? (
          <Alert severity="info">
            &quot;{selectedOption.displayName}&quot; is in the published catalog but has no config:service row for
            label &quot;{normalizedLabel}&quot;. Sync adapter config or import runtime config before editing routing.
          </Alert>
        ) : rows.length > 0 && enabledVendorCount > 1 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Multiple vendors are enabled for this service. The adapter will try them in this vendor-level priority order.
          </Alert>
        ) : null}

        {serviceOptions.length > 0 && selectedOption?.hasAdapterConfig && rows.length === 0 ? (
          <Alert severity="info">
            No providers in config:service:{routingServiceKey} for label &quot;{normalizedLabel}&quot;.
          </Alert>
        ) : null}

        {rows.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Priority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice()
                .sort((a, b) => a.priority - b.priority || a.vendorId.localeCompare(b.vendorId))
                .map((row, index) => (
                  <TableRow key={row.vendorId} hover>
                    <TableCell sx={{ width: 120 }}>
                      <IconButton size="small" onClick={() => moveRow(row.vendorId, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveRow(row.vendorId, 1)} disabled={index === rows.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {row.vendorId}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={row.enabled}
                            onChange={event => setRow(row.vendorId, { enabled: event.target.checked })}
                          />
                        }
                        label={row.enabled ? 'Enabled' : 'Disabled'}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 160 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={row.priority}
                        onChange={event => {
                          const next = parsePriorityInput(event.target.value);
                          if (next !== null) setRow(row.vendorId, { priority: next });
                        }}
                        inputProps={{ min: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : null}

        {updateAdapterRouting.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Vendor switching order saved to adapter_configs for label &quot;{normalizedLabel}&quot;.
          </Alert>
        )}
        {updateAdapterRouting.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {messageFromAdminConsoleAxiosError(updateAdapterRouting.error) ||
              'Failed to save vendor switching order.'}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Step</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Customer mode</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Spec</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(q.data ?? []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.doctrineStepId}</TableCell>
                <TableCell>{row.tenantId ?? 'ANY'}</TableCell>
                <TableCell>{row.productCode ?? 'ANY'}</TableCell>
                <TableCell>{row.channel}</TableCell>
                <TableCell>{row.customerMode}</TableCell>
                <TableCell>{row.strategy}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 360 }}>
                  {JSON.stringify(row.spec)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.isActive ? 'success' : 'default'}
                    label={row.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
